import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { CheckCircle, Upload, Zap, Users, Shield, Clock, ArrowRight, Mail, Star, ChevronDown, Menu, X, ThumbsDown, Sparkles } from 'lucide-react';

// Import new components
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Main Landing Page Component
function LandingPage() {
  const [email, setEmail] = useState('');
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showWaitlistPopup, setShowWaitlistPopup] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    const formData = new FormData();
    formData.append('email_address', email);

    try {
      const response = await fetch('https://app.kit.com/forms/8728991/subscriptions', {
        method: 'POST',
        body: formData,
        mode: 'no-cors' // ConvertKit handles CORS
      });

      // Since mode is 'no-cors', we can't read the response, but submission likely succeeded
      setShowWaitlistPopup(true);
      setSubmitMessage('');
      setEmail('');
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "What types of veterinary records can I upload?",
      answer: "You can upload PDF files from any veterinary clinic including exam reports, lab results, surgery notes, vaccination records, and invoices. Our AI works with records from all major vet clinics and formats."
    },
    {
      question: "How accurate is the AI extraction?",
      answer: "Our AI achieves 95%+ accuracy in extracting key information like visit dates, diagnoses, medications, and procedures. You can always review and edit any extracted information before saving."
    },
    {
      question: "Can I correct mistakes?",
      answer: "Yes – AI can make mistakes. The AI will flag low-level confidence fields. We will also flag human generated issues from the original record. We will flag issues - you will verify the record."
    },
    {
      question: "Is my pet's health data secure?",
      answer: "Absolutely. We use enterprise-grade encryption, secure cloud storage, and never share your data with third parties."
    },
    {
      question: "Can I manage multiple pets?",
      answer: "Yes! Create profiles for all your pets - dogs & cats. Each pet gets their own organized timeline and health history."
    },
    {
      question: "Can I cancel at any time?",
      answer: "Yes. You can cancel your subscription at any time with no questions asked. If you want to download the original PDFs, please do so before."
    },
    {
      question: "What if my records are messy or hard to read?",
      answer: "We recommend getting the records as PDF format from your vet for optimal accuracy. We accept all types of records. You will verify low-confidence records."
    },
    {
      question: "How do I get my money back?",
      answer: "If you're not completely satisfied, we'll refund your full payment, no questions asked. We're confident you'll love how organized your pet's health records become."
    },
    {
      question: "Do you offer a free trial?",
      answer: "We don't offer a free trial, but we do offer a 14-day money-back guarantee. This ensures you have full access to all features and can make the most of organizing your pet's records."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <span className="text-3xl">🐾</span>
              <img 
                src="https://pub-b9622d81b0bd442fb020f880d8cec37d.r2.dev/petrecord-logo.png" 
                alt="PetRecord.ai" 
                className="h-8"
              />
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              <a href="#how-it-works" className="text-gray-600 hover:text-gray-900 transition-colors">How It Works</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 transition-colors">Pricing</a>
              <a href="#faq" className="text-gray-600 hover:text-gray-900 transition-colors">FAQ</a>
              <Button variant="cta" size="sm">Join Waitlist</Button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden pb-4 border-t border-gray-200 mt-4 pt-4">
              <div className="flex flex-col space-y-4">
                <a 
                  href="#how-it-works" 
                  className="text-gray-600 hover:text-gray-900 transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  How It Works
                </a>
                <a 
                  href="#pricing" 
                  className="text-gray-600 hover:text-gray-900 transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Pricing
                </a>
                <a 
                  href="#faq" 
                  className="text-gray-600 hover:text-gray-900 transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  FAQ
                </a>
                <Button variant="cta" size="sm" className="w-full">
                  Join Waitlist
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-gradient py-16 lg:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-4 leading-[1.1]">
                Finally, Pet Health Records That
                <span className="bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent block">Make Sense</span>
              </h1>
              <p className="text-xl text-custom-gray mb-4 leading-relaxed">
                In minutes, your pet's records go from <span className="font-bold text-custom-gray-400">scattered and technical</span> to <span className="font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">organized and human</span>.
              </p>
              <p className="text-lg text-custom-gray mb-10 leading-relaxed">
                Built for owners who want <span className="font-bold">clarity</span>, not confusion.
              </p>

              {/* Trust Indicators with Checkmarks - Moved above form */}
              <div className="grid grid-cols-2 gap-2 mb-8 max-w-xl mx-auto lg:mx-0">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span>14-day money-back guarantee</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span>Secure & private</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span>Unlimited records</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span>AI-powered organization</span>
                </div>
              </div>

              {/* Pixel-Perfect Email Form */}
              <div className="max-w-xl mx-auto lg:mx-0 lg:ml-1">
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="flex-[2] min-h-[48px] px-4 py-4 border border-gray-400 rounded-lg text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:shadow-md transition-all duration-200"
                      required
                      disabled={isSubmitting}
                    />
                    <Button type="submit" variant="cta" size="xl" className="flex-1 sm:flex-none flex items-center justify-center gap-2 whitespace-nowrap min-h-[48px]" disabled={isSubmitting}>
                      {isSubmitting ? 'Starting...' : 'Start Organizing'}
                      <ArrowRight className="w-5 h-5" />
                    </Button>
                  </div>
                  {submitMessage && (
                    <div className={`text-sm px-4 py-2 rounded-lg ${submitMessage.includes('Success') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {submitMessage}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            
            {/* Before Column */}
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                  Before PetRecord.ai
                </h2>
                <p className="text-xl text-red-600">The Old Way: Messy, Confusing & Not Built for You</p>
              </div>

              <div className="space-y-6">
                <Card className="p-6 border border-red-200">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <ThumbsDown className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Records scattered & disorganized</h3>
                      <p className="text-gray-600">Paper, email attachments, downloaded to different devices - never in one place.</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 border border-red-200">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <ThumbsDown className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Made for vets, not you</h3>
                      <p className="text-gray-600">Records are filled with jargon, burying what actually matters to you.</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 border border-red-200">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <ThumbsDown className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Errors nobody catches</h3>
                      <p className="text-gray-600">Wrong or missing info, contradictions - no one double-checks.</p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>

            {/* After Column */}
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent mb-4">
                  After PetRecord.ai
                </h2>
                <p className="text-xl text-blue-600">The New Way: Simple, Organized & Owner-Friendly</p>
              </div>

              <div className="space-y-6">
                <Card className="p-6 border border-blue-200 bg-gradient-to-r from-blue-50 to-blue-50">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Everything organized instantly</h3>
                      <p className="text-gray-600">AI sorts by visit, date, and clinic - giving you records & timeline that finally makes sense.</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 border border-blue-200 bg-gradient-to-r from-blue-50 to-blue-50">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Clear owner-first summaries</h3>
                      <p className="text-gray-600">Action items & notes that highlight what matters to you.</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 border border-blue-200 bg-gradient-to-r from-blue-50 to-blue-50">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Built-in error checks</h3>
                      <p className="text-gray-600">Flags missing or inconsistent data so you can verify fast.</p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="section-gradient py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">From chaos to clarity in 3 simple steps</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <Card className="text-center p-8 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  1
                </div>
              </div>
              <div className="mt-4 mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <Upload className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Upload Your Records</h3>
              <p className="text-gray-600">Simply upload records from your vet. Works with records from any clinic, any format. (PDF records are ideal)</p>
            </Card>

            <Card className="text-center p-8 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  2
                </div>
              </div>
              <div className="mt-4 mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <Zap className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">AI Extracts & Organizes</h3>
              <p className="text-gray-600">Our AI reads through the medical jargon and pulls out what matters: dates, diagnoses, treatments, next steps.</p>
            </Card>

            <Card className="text-center p-8 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  3
                </div>
              </div>
              <div className="mt-4 mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <Clock className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Get Your Timeline</h3>
              <p className="text-gray-600">View your pet's complete health story in chronological order. Search, filter, and understand everything.</p>
            </Card>
          </div>

          <div className="text-center mt-12">
            <form onSubmit={handleEmailSubmit} className="space-y-4 max-w-md mx-auto">
              <div className="flex gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  required
                  disabled={isSubmitting}
                />
                <Button type="submit" variant="cta" size="lg" className="flex items-center gap-2" disabled={isSubmitting}>
                  {isSubmitting ? 'Starting...' : 'Start Now'}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </div>
              {submitMessage && (
                <div className={`text-sm px-4 py-2 rounded-lg ${submitMessage.includes('Success') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {submitMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Simple, Honest Pricing</h2>
          <p className="text-xl text-gray-600 mb-12">One plan. All features. No hidden fees.</p>

          <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            <Card className="p-8 relative">
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">Monthly</CardTitle>
                <div className="text-4xl font-bold text-gray-900 mb-2">$9<span className="text-lg text-gray-600">/month</span></div>
              </CardHeader>
              <CardContent>
                <Button variant="outline" className="w-full mb-4">Get Started</Button>
                <p className="text-sm text-gray-600">Billed monthly • Cancel anytime</p>
              </CardContent>
            </Card>

            <Card className="p-8 border-2 border-indigo-600 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-indigo-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                Best Value
              </div>
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">Annual</CardTitle>
                <div className="text-4xl font-bold text-gray-900 mb-2">$88<span className="text-lg text-gray-600">/year</span></div>
                <p className="text-green-600 font-semibold">Save $20 per year</p>
              </CardHeader>
              <CardContent>
                <Button variant="cta" className="w-full mb-4">Get Started</Button>
                <p className="text-sm text-gray-600">Billed annually • 2 months free</p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 space-y-4">
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>14-day money-back guarantee</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>Secure & private - your data stays yours</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>Unlimited records</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>AI-powered organization</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section-gradient py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Everything you need to know about PetRecord.ai</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index}>
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full p-6 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                >
                  <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left">
            <h2 className="text-sm font-semibold text-gray-500 mb-8">Our Story</h2>
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">
              Built by a pet parent who's been there
            </h3>
            <div className="prose prose-lg max-w-none text-gray-600 mb-8 leading-relaxed">
              <p className="mb-6">
                After moving, I lost track of my pet's vet records. I had to email old clinics, dig up phone numbers, 
                and bounce between Google Drive, email attachments, and random files just to piece things together.
              </p>
              <p className="mb-6">
                Even when the records were "organized," I had to open each one and sift through pages of jargon - 
                sometimes 5+ pages - just to find a single detail that mattered. I searched for a solution and couldn't 
                find one.
              </p>
              <p className="font-semibold text-gray-900">So I built one.</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-full flex items-center justify-center">
                <span className="text-2xl">🐕</span>
              </div>
              <div>
                <div className="font-semibold text-gray-900 text-lg">Sarah & Luna</div>
                <div className="text-gray-600">Founder & Chief Happiness Officer</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="bg-indigo-600 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Ready to Finally Organize Your Pet's Health Records?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Join thousands of pet owners who've transformed chaos into clarity with PetRecord.ai
          </p>
          <form onSubmit={handleEmailSubmit} className="space-y-4 max-w-lg mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email to get started"
                className="flex-1 px-4 py-4 border border-indigo-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
                required
                disabled={isSubmitting}
              />
              <Button type="submit" variant="secondary" size="xl" className="bg-white text-indigo-600 hover:bg-gray-100 font-semibold" disabled={isSubmitting}>
                {isSubmitting ? 'Starting...' : 'Start Organizing Today'}
              </Button>
            </div>
            {submitMessage && (
              <div className={`text-sm px-4 py-2 rounded-lg ${submitMessage.includes('Success') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {submitMessage}
              </div>
            )}
          </form>
          <p className="text-indigo-200 mt-4">
            14-day money-back guarantee • Get organized in minutes
          </p>
        </div>
      </section>

      {/* Footer with Email Opt-in */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <span className="text-3xl">🐾</span>
                <img 
                  src="https://pub-b9622d81b0bd442fb020f880d8cec37d.r2.dev/PetRecord-dark.png" 
                  alt="PetRecord.ai" 
                  className="h-8"
                />
              </div>
              <p className="text-gray-300 mb-4">
                AI-powered pet health record organization. Finally, vet records that make sense to pet owners.
              </p>
              <p className="text-gray-400 text-sm mb-6">
                Disclaimer: Not veterinary advice. PetVault organizes your records and generates AI summaries. Always consult a licensed veterinarian for care decisions.
              </p>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                  <Mail className="w-4 h-4" />
                </div>
                <div className="text-gray-300">
                  Contact: <a href="mailto:team@petrecord.ai" className="font-bold hover:text-white transition-colors">team@petrecord.ai</a>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Get Early Access Updates</h4>
              <p className="text-gray-300 mb-4">
                Be the first to know about new features and pet health insights.
              </p>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="flex gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    required
                    disabled={isSubmitting}
                  />
                  <Button type="submit" variant="cta" disabled={isSubmitting}>
                    {isSubmitting ? 'Subscribing...' : 'Sign Up'}
                  </Button>
                </div>
                {submitMessage && (
                  <div className={`text-sm px-4 py-2 rounded-lg ${submitMessage.includes('Success') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {submitMessage}
                  </div>
                )}
              </form>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} PetRecord.ai. All rights reserved. • <a href="/privacy" className="hover:text-gray-200">Privacy Policy</a> • <a href="/terms" className="hover:text-gray-200">Terms of Service</a></p>
          </div>
        </div>
      </footer>

      {/* Alex Hormozi-Style Waitlist Popup */}
      {showWaitlistPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center shadow-2xl transform transition-all">
            <div className="mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                You're In! 🎉
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                <span className="font-bold text-blue-600">Launching Summer 2026.</span>
              </p>
            </div>

            <div className="bg-blue-50 rounded-xl p-6 mb-6">
              <h3 className="font-bold text-gray-900 mb-3 text-left">Here's what happens next:</h3>
              <div className="space-y-3 text-left">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">1</span>
                  </div>
                  <p className="text-gray-700 text-sm">
                    <span className="font-semibold">You're on the list.</span> No more chaotic vet records.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">2</span>
                  </div>
                  <p className="text-gray-700 text-sm">
                    <span className="font-semibold">We build the most insane pet health organizer.</span> While you wait.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">3</span>
                  </div>
                  <p className="text-gray-700 text-sm">
                    <span className="font-semibold">You get first access</span> when we launch Summer 2026.
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <p className="text-xs text-gray-500 mb-4">
                P.S. We'll only email you when it's ready. No spam. Promise.
              </p>
              <Button
                onClick={() => setShowWaitlistPopup(false)}
                variant="cta"
                size="lg"
                className="w-full"
              >
                I Can't Wait! 🚀
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Main App Component with Routing
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
      </Routes>
    </Router>
  );
}

export default App;